import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { TableItem, TableRowType } from "../../../main/components/RopTable/RopTable";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { GeracaoRelatorioStatusEnum, RelatorioContent, RelatorioItem, Usina } from "../types";
import { useContext } from "react";
import { MainContext } from "../../../main/context/MainContextProvider";
import { GetDateStringFromInterval } from "../pages/ReportList";
import { CardIntervalOptionsEnum } from "../../dashboard/types";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { formatDataHoraCaptura, getDayMonthAndYearFromDate } from "../../../main/utils/utils";
import { usinasApi } from "../../../services/api";
import { useNavigate } from "react-router-dom";
import RopCollapsibleTable from "../../../main/components/RopCollapsibleTable/RopCollapsibleTable";
import { CollapsibleTableRowType } from "../../../main/components/RopCollapsibleTable/RopTableCollapsibleBody";
import { TypeInfo } from "../../../main/components/TypeInfo/TypeInfo";

export type HistoryListProps = {
    loading: boolean,
    reportItemList: RelatorioContent[],
    interval: CardIntervalOptionsEnum,
    setOpenReportErrorsModal: React.Dispatch<React.SetStateAction<boolean>>,
    setSelectedReportItem: React.Dispatch<React.SetStateAction<RelatorioItem | null>>,
    onMount?: () => void;
    usinas: Usina[];
}

export default function HistoryList(props: HistoryListProps) {
    const {
        toastNotifications,
        setToastNotifications,
        usinasIdsLoading,
        setUsinasIdsLoading,
        theme
    } = useContext(MainContext);

    const primary = theme.muiTheme.palette.primary.main;

    const { loading, reportItemList, interval, setOpenReportErrorsModal, setSelectedReportItem, onMount, usinas } = props;

    const navigate = useNavigate();

    const handleGetReport = async (pdfUrl: string) => {
        const downloadButton = document.createElement("a");
        downloadButton.href = pdfUrl;
        downloadButton.download = `relatorio-diario-${pdfUrl}.pdf`;
        downloadButton.click();
    };

    const getRelatorioDiario = async (usinaId: string, date: string | Date) => {
        const currentDate = getDayMonthAndYearFromDate(date);
        setUsinasIdsLoading([
            ...(usinasIdsLoading ?? []),
            {
                date: String(date),
                usinaId: usinaId,
                interval: CardIntervalOptionsEnum.Dia
            }
        ]);
        const newNotifications = {
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "A busca foi inicializada, voce será notificado assim que ela for finalizada!",
                },
            ],
        };
        setToastNotifications(newNotifications);
        try {
            await usinasApi.generateDailyReport(
                `${currentDate.ano}-${currentDate.mes}-${currentDate.dia}`,
                [usinaId]
            );
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "success" as const,
                        text: (
                            <>
                                Relatório do dia{" "}
                                {`${currentDate.dia}/${currentDate.mes}/${currentDate.ano}`} foi
                                gerado com sucesso!{" "}
                                <a
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/usinas/relatorios")}
                                >
                                    Clique aqui
                                </a>{" "}
                                para voltar a lista de relatórios.
                            </>
                        ),
                    },
                ],
            };
            setToastNotifications(newNotifications);
            onMount && onMount()
        } catch (error) {
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "error" as const,
                        text: "Erro ao gerar relatório diário, tente novamente!",
                    },
                ],
            };
            setToastNotifications(newNotifications);
        } finally {
            setUsinasIdsLoading(
                usinasIdsLoading?.filter(
                    (usina) => usina.usinaId != usinaId && usina.date != String(date) && usina.interval != CardIntervalOptionsEnum.Dia
                )
            );
        }
    };

    const getRelatorioMensal = async (usinaId: string, date: string | Date) => {
        const currentDate = getDayMonthAndYearFromDate(date);
        setUsinasIdsLoading([
            ...(usinasIdsLoading ?? []),
            {
                date: String(date),
                usinaId: usinaId,
                interval: CardIntervalOptionsEnum.Mes
            }
        ]);
        const newNotifications = {
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "A busca foi inicializada, voce será notificado assim que ela for finalizada!",
                },
            ],
        };
        setToastNotifications(newNotifications);
        try {
            await usinasApi.generateMonthlyReport(new Date(date), [usinaId]);

            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "success" as const,
                        text: (
                            <>
                                Relatório do mês {`${currentDate.mes}/${currentDate.ano}`}{" "}
                                gerado com sucesso!{" "}
                                <a
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/usinas/relatorios")}
                                >
                                    Clique aqui
                                </a>{" "}
                                para voltar a lista de relatórios.
                            </>
                        ),
                    },
                ],
            };
            setToastNotifications(newNotifications);
            onMount && onMount()
        } catch (error) {
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "error" as const,
                        text: "Erro ao gerar relatório mensal, tente novamente!",
                    },
                ],
            };
            setToastNotifications(newNotifications);
        } finally {
            setUsinasIdsLoading(
                usinasIdsLoading?.filter(
                    (usina) => usina.usinaId != usinaId && usina.date != String(date) && usina.interval != CardIntervalOptionsEnum.Mes
                )
            );
        }
    };

    const items: CollapsibleTableRowType[] = reportItemList.map((report) => {

        const currentUsina = usinas.find((usina) => usina.usinaIds.id === report.usinaId)

        const usinaHasPermission = () => {
            if (interval == CardIntervalOptionsEnum.Dia && currentUsina?.reportOptions?.dailyReportEnabled) {
                return true
            }
            if (interval == CardIntervalOptionsEnum.Mes && currentUsina?.reportOptions?.monthlyReportEnabled) {
                return true
            }
            return false
        }

        const reportIsLoading = !!usinasIdsLoading?.find(
            (usn) =>
                usn.usinaId === report.usinaId && usn.date === String(report.date) && usn.interval === interval
        );

        const getButtonTooltipText = (text: string) => {
            if (!usinaHasPermission()) {
                if (interval == CardIntervalOptionsEnum.Dia)
                    return (<>
                        A usina não tem permissão para relatório diário,{" "}
                        <Box
                            sx={{ cursor: "pointer", color: primary }}
                            onClick={() => navigate(`/usinas/${report.usinaId}`)}
                        >
                            Clique aqui para alterar a permissão da usina
                        </Box>
                    </>)
                if (interval == CardIntervalOptionsEnum.Mes)
                    return (<>
                        A usina não tem permissão para relatório mensal,{" "}
                        <Box
                            sx={{ cursor: "pointer", color: primary }}
                            onClick={() => navigate(`/usinas/${report.usinaId}`)}
                        >
                            Clique aqui para alterar a permissão da usina
                        </Box>
                    </>)
            }
            return text
        }

        const rowItems: TableItem[] = [
            {
                item: report.usinaName,
                align: "center",
                isMobile: true,
            },
            {
                item: GetDateStringFromInterval(interval, report.date),
                align: "center",
                isMobile: true,
            },
            {
                item: report.hasReportToDate ? (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Tooltip
                            title={getButtonTooltipText(!report.pdfUrl ? "O Relatório não possui PDF!" : "")}
                        >
                            <Box
                                sx={{
                                    width: { sx: "100%", md: "49%" },
                                    marginBottom: { xs: "8px", md: "0px" },
                                }}
                            >
                                <PrimaryBaseButton
                                    disabled={!usinaHasPermission() || !report.pdfUrl}
                                    sx={{ width: "100%" }}
                                    title="Exportar PDF"
                                    fontSize={"13px"}
                                    onClick={() => handleGetReport(report.pdfUrl)}
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Tooltip
                            title={getButtonTooltipText("")}
                        >
                            <Box
                                sx={{
                                    width: { sx: "100%", md: "49%" },
                                    marginBottom: { xs: "8px", md: "0px" },
                                }}
                            >
                                <PrimaryBaseButton
                                    disabled={!usinaHasPermission() || reportIsLoading}
                                    loading={reportIsLoading}
                                    sx={{ width: "100%" }}
                                    title="Gerar PDF"
                                    fontSize={"13px"}
                                    onClick={() => {
                                        report.interval == CardIntervalOptionsEnum.Dia
                                            ? getRelatorioDiario(report.usinaId, report.date)
                                            : getRelatorioMensal(report.usinaId, report.date);
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                ),
                align: "center",
                isMobile: true,
                width: "30%"
            }
        ]

        const historyHeaders: TableItem[] = [
            {
                item: "Usina",
                align: "center",
                isMobile: true,
            },
            {
                item: "Data de Geração",
                align: "center",
                isMobile: true,
            },
            {
                item: "Status da Geração",
                align: "center",
                isMobile: true,
            },
            {
                item: "Erros",
                align: "center",
                isMobile: true,
            },
        ]

        const historyItems: TableRowType[] = report.relatorioHistory.map((history: RelatorioItem) => {

            const hasErrors = history.errors.length > 0;
            return {
                rowItems: [
                    {
                        item: history.usinaName,
                        align: "center",
                        isMobile: true,
                    },
                    {
                        item: <>{formatDataHoraCaptura(history.createdAt.toString())}</>,
                        align: "center",
                        isMobile: true,
                    },
                    {
                        item:
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <TypeInfo.Root
                                    backgroundColor={
                                        loading
                                            ? "#A8B6BC"
                                            : history.status == GeracaoRelatorioStatusEnum.Success
                                                ? "#27AE60"
                                                : "#FF0000"
                                    }
                                    width="fit-content"
                                >
                                    <TypeInfo.Text
                                        sx={{ fontSize: { xs: "10px", sm: "13px" } }}
                                        text={
                                            loading
                                                ? "Carregando..."
                                                : history.status == GeracaoRelatorioStatusEnum.Success
                                                    ? "Sucesso"
                                                    : "Erro"
                                        }
                                    />
                                </TypeInfo.Root>
                            </Box>,
                        align: "center",
                        isMobile: true,
                    },
                    {
                        item: <>
                            <Tooltip title={hasErrors ? "Ver Erros" : "Nenhum Erro Encontrado"}>
                                <Box sx={{ cursor: "pointer" }}>
                                    <IconButton
                                        color="inherit"
                                        onClick={() => {
                                            setSelectedReportItem(history)
                                            setOpenReportErrorsModal(true)
                                        }}
                                        disabled={!hasErrors}
                                        sx={{
                                            padding: "5px"
                                        }}
                                    >
                                        <ReportProblemIcon
                                            style={{
                                                transition: "transform 0.3s ease-in-out",
                                                color: hasErrors ? "#FF0000" : "#656C72",
                                            }}
                                        />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        </>,
                        align: "center",
                        isMobile: true,
                    }
                ],
            }
        })

        return {
            rowItems: rowItems,
            historyHeaders: historyHeaders,
            historyRows: historyItems
        }
    })

    return (
        <Grid item xs={12} >
            <RopCollapsibleTable
                sxCardBox={{ marginLeft: { xs: "10px", md: "0px" }, width: "100%" }}
                hover
                itemsHeaders={[
                    {
                        item: "",
                        align: "center",
                        isMobile: true,
                        width: "10%"
                    },
                    {
                        item: "Usina",
                        align: "center",
                        isMobile: true,
                    },
                    {
                        item: "Data",
                        align: "center",
                        isMobile: true,
                    },
                    {
                        item: "PDF",
                        align: "center",
                        isMobile: true,
                        width: "30%"
                    }
                ]}
                items={items}
                isLoading={loading}
                title="Histórico de Geração"
            />
        </Grid>
    )
}